import type { ApprovalRow, EventRow } from '../../lib/database.types'
import { at, obj, str } from '../../lib/json'
import { renderEvent, resolve, type Registry } from './eventTypes'

/**
 * The reports the fleet has generated, read out of `events` and the event-type
 * registry.
 *
 * A generated report is not a type name here. It is an event whose payload
 * carries a `report_type`, a `period` and an `artifact_url` -- a thing that was
 * made, for a span of time, and can be opened again. Selected by that
 * structure, the way `map/approvals.ts` selects its adapters, so the finance
 * lane's weekly review and whatever reports next week with the same three keys
 * both land here with no change in this file (product rule 2).
 *
 * What became of one is the same question one level on: the later events for
 * the same run say it was cleared, sent, held or could not be sent, and the
 * registry already holds the sentence each of those reads as. Nothing in this
 * module writes a label, and nothing switches on a report type, an entity or a
 * department.
 *
 * Three literals are written here and none of them is a report type:
 * `DECISION_EVENT_TYPE`, because a decision is recorded against the approval
 * rather than against the report; `HELD` and `TURNED_DOWN`, which say what no
 * event says -- the absence of any signal, and a decision state the registry
 * has no type for.
 */

/**
 * The decision the queue writes, which is the one event type named literally in
 * this module.
 *
 * It is not a report event and carries no `report_type`: it is written for
 * every closed approval in the fleet, by `decide_approval()` and by the
 * watchdog's machine closures alike. It reaches a report only through its
 * `run_id`, which is why the read that fetches it is narrowed to the runs the
 * generations name rather than to the type.
 */
export const DECISION_EVENT_TYPE = 'approval.decided'

/**
 * How long a report's link keeps working, in days.
 *
 * `artifact_url` is served from `finance_report_artifacts`, whose rows carry an
 * `expires_at` thirty days after they are written. This app cannot check it:
 * `authenticated` holds no grant on that table and this ticket does not change
 * that. So the age of the generation is the only thing there is to go on, and a
 * row past it says the link has probably gone rather than offering one that
 * fails. Keeping reports longer is a fleet-side decision.
 */
export const ARTIFACT_LIFETIME_DAYS = 30

const DAY_MS = 24 * 60 * 60_000

/**
 * The two placeholders that identify a report's own event types in the
 * registry.
 *
 * A type whose sentence is about a report names both of them: it says which
 * report and for which period. The registry's seven such types today span three
 * categories -- lifecycle, gate and failure -- so the category cannot do this
 * job, and the two types that are about a report but carry neither placeholder
 * (`{label}: {summary}`) are correctly left out: they are not a generation and
 * say nothing about what became of one.
 */
const REPORT_PLACEHOLDERS = ['{payload.report_type}', '{payload.period}'] as const

/**
 * The registry's category for a report that moved, as opposed to one waiting at
 * a gate or one that failed.
 *
 * This is how a row is known to have reached somewhere without naming the type
 * that took it there: a lifecycle event is the report moving, so the next
 * report type's own "sent" event settles its row with no change here.
 */
const MOVED_CATEGORY = 'lifecycle'

/** Decision states that mean a person said no. The table's own vocabulary. */
const TURNED_DOWN_STATES = new Set(['dismissed', 'rejected'])

/** What a generation reads as before anything else has been recorded about it. */
const HELD = 'held, no decision recorded'

/** What a turn-down reads as. The registry has no type for one, so this is said here, once. */
const TURNED_DOWN = 'turned down'

export interface GeneratedReport {
  /** The generating event's id: two generations of one period are two rows. */
  key: string
  /** The fleet's own name for the report, as its payload wrote it. */
  reportType: string
  /** The span it covers, as its payload wrote it -- a week-ending date today. */
  period: string
  generatedAt: string
  /** One line: the registry's sentence for the latest signal, or what no event said. */
  status: string
  /** Something later said it went out or was turned down. Everything else reads quieter. */
  settled: boolean
  artifactUrl: string
  /** Generated longer ago than the artifact lifetime, so the link has probably gone. */
  expired: boolean
}

/** The event types whose sentences are about a report, as the registry defines them. */
export function reportEventTypes(registry: Registry): string[] {
  return registry.defs
    .filter((d) => REPORT_PLACEHOLDERS.every((p) => d.template.includes(p)))
    .map((d) => d.type)
    .sort()
}

interface Generation {
  event: EventRow
  at: number
  reportType: string
  period: string
  artifactUrl: string
}

/**
 * The event as a generation, or null if it is not one.
 *
 * All three keys or nothing. A payload with no `artifact_url` is a report
 * nobody can open -- the staging call did not come back with a link -- and a
 * row offering no link, no period or no name would be a line that answers none
 * of the questions this list exists for.
 */
function asGeneration(event: EventRow): Generation | null {
  const reportType = str(at(event.payload, 'report_type'))
  const period = str(at(event.payload, 'period'))
  const artifactUrl = str(at(event.payload, 'artifact_url'))
  const ts = Date.parse(event.ts)
  if (!reportType || !period || !artifactUrl || Number.isNaN(ts)) return null
  return { event, at: ts, reportType, period, artifactUrl }
}

/**
 * The runs the generations belong to -- what the decisions read is narrowed by,
 * so it fetches the decisions about these reports rather than every decision
 * the fleet has ever recorded.
 */
export function generationRunIds(events: ReadonlyArray<EventRow>): string[] {
  const ids = new Set<string>()
  for (const e of events) {
    if (asGeneration(e) && e.run_id) ids.add(e.run_id)
  }
  return [...ids].sort()
}

/** Something later that says what became of a generation. */
interface Signal {
  at: number
  runId: string | null
  /** For the fallback join, when a signal carries no run id. */
  reportType: string | null
  period: string | null
  status: string
  turnedDown: boolean
  moved: boolean
}

/**
 * A signal's wording comes from the registry -- the same rendering the activity
 * feed uses, so a type's sentence is written in one place and that place is
 * `public.event_types`. A type the registry does not know falls back to the
 * event's own summary, exactly as the feed does: an unregistered name is
 * something to notice, not something to hide.
 */
function signalFromEvent(event: EventRow, registry: Registry): Signal | null {
  const ts = Date.parse(event.ts)
  if (Number.isNaN(ts)) return null
  const res = resolve(registry, event.type)
  const def = res.kind === 'event' ? res.def : null
  const turnedDown =
    event.type === DECISION_EVENT_TYPE &&
    TURNED_DOWN_STATES.has(str(at(event.payload, 'state')) ?? '')
  return {
    at: ts,
    runId: event.run_id,
    reportType: str(at(event.payload, 'report_type')),
    period: str(at(event.payload, 'period')),
    status: turnedDown ? TURNED_DOWN : def ? renderEvent(def, event) : event.summary ?? event.type,
    turnedDown,
    moved: def?.category === MOVED_CATEGORY,
  }
}

/**
 * A turn-down read off the queue row itself, for the ones that left no
 * `approval.decided` event behind: five of the thirteen decided rows had none
 * before 24 Sep 2026, and the w/e 2026-10-24 dismissal decided on 30 Oct still
 * has none.
 *
 * Selected by structure, like everything else here: a queue row carrying a
 * `report` block is a send the fleet was holding, whatever its source string
 * says. A row that was closed rather than turned down adds nothing -- the
 * lane's own events already say what happened next -- so only the two refusing
 * states produce a signal.
 */
function signalFromApproval(row: ApprovalRow): Signal | null {
  if (!row.run_id || !row.decided_at) return null
  if (!TURNED_DOWN_STATES.has(row.state)) return null
  if (!obj(at(row.context, 'report'))) return null
  const ts = Date.parse(row.decided_at)
  if (Number.isNaN(ts)) return null
  return {
    at: ts,
    runId: row.run_id,
    reportType: null,
    period: null,
    status: TURNED_DOWN,
    turnedDown: true,
    moved: false,
  }
}

/**
 * The generation a signal belongs to, from a list ordered newest first.
 *
 * The run is the precise join and is tried first: w/e 2026-10-24 was generated
 * twice, by two runs, with two links and two different outcomes, and anything
 * joining on the period alone merges them into one row that is true of neither.
 * A signal with no run id falls back to the report and the period, and takes
 * the newest generation that was already made when it arrived -- the one it can
 * actually be about.
 */
function ownerOf(signal: Signal, newestFirst: ReadonlyArray<Generation>): Generation | null {
  if (signal.runId) {
    return newestFirst.find((g) => g.event.run_id === signal.runId && g.at <= signal.at) ?? null
  }
  if (!signal.reportType || !signal.period) return null
  return (
    newestFirst.find(
      (g) => g.reportType === signal.reportType && g.period === signal.period && g.at <= signal.at,
    ) ?? null
  )
}

/**
 * One row per generated report, newest first, with what became of it.
 *
 * `events` is every row the two reads returned: the generations, the lane's
 * later signals and the decisions. They are told apart by their shape rather
 * than by their names, so an event this module has never heard of is either a
 * generation (it carries the three keys) or a signal about one (it carries a
 * run the generations name), and in neither case does it need code here.
 */
export function toReports(
  events: ReadonlyArray<EventRow>,
  approvals: ReadonlyArray<ApprovalRow>,
  registry: Registry,
  now: number = Date.now(),
): GeneratedReport[] {
  const generations: Generation[] = []
  const signals: Signal[] = []
  for (const e of events) {
    const generation = asGeneration(e)
    if (generation) generations.push(generation)
    else {
      const signal = signalFromEvent(e, registry)
      if (signal) signals.push(signal)
    }
  }
  for (const row of approvals) {
    const signal = signalFromApproval(row)
    if (signal) signals.push(signal)
  }

  generations.sort((a, b) => b.at - a.at)

  // The latest signal by ts wins: a report that was held on stale data on
  // Monday and sent on Tuesday reads as sent.
  const latest = new Map<Generation, Signal>()
  for (const signal of signals) {
    const owner = ownerOf(signal, generations)
    if (!owner) continue
    const held = latest.get(owner)
    if (!held || signal.at >= held.at) latest.set(owner, signal)
  }

  const lifetime = ARTIFACT_LIFETIME_DAYS * DAY_MS
  return generations.map((g) => {
    const signal = latest.get(g)
    return {
      key: String(g.event.id),
      reportType: g.reportType,
      period: g.period,
      generatedAt: g.event.ts,
      status: signal ? signal.status : HELD,
      settled: signal ? signal.turnedDown || signal.moved : false,
      artifactUrl: g.artifactUrl,
      expired: now - g.at > lifetime,
    }
  })
}
